import { read } from 'gso-session';
import { parse } from 'content-type';

class FetchError extends Error {
    constructor(input, options) {
        super();
        this.name = 'FetchError';
        this.options = Object.assign({ input }, options);
    }
}
/**
 * Error thrown when failing to fetch a resource.
 * /!\ The request could not be sent.
 */
class NetworkError extends FetchError {
    constructor(input, options, original) {
        super(input, options);
        this.name = 'NetworkError';
        this.message = original.message;
        this.original = original;
    }
}
/**
 * Error thrown when the server failed (>= 500).
 */
class ServerError extends FetchError {
    constructor(input, options, response) {
        super(input, options);
        this.name = 'ServerError';
        this.message = `${response.status} - ${response.statusText}`;
        this.response = response && json(response);
    }
}
/**
 * Error thrown when the client failed (>= 400).
 */
class ClientError extends FetchError {
    constructor(input, options, response) {
        super(input, options);
        this.name = 'ClientError';
        this.message = `${response.status} - ${response.statusText}`;
        this.response = response && json(response);
    }
}

/**
 * Small wrapper around the fetch API
 */
// As desribed in https://github.com/matthew-andrews/isomorphic-fetch/issues/107
const networkErrorMessages = [
    'NetworkError when attempting to fetch resource.',
    'Failed to fetch',
    'Network request failed' // IE
];
let beforeHook = (_) => { };
let afterHook = (_, error) => {
    if (error) {
        throw error;
    }
};
function status(input, options) {
    return (response) => {
        if (response.status >= 500) {
            throw new ServerError(input, options, response);
        }
        if (response.status >= 400) {
            throw new ClientError(input, options, response);
        }
        return response;
    };
}
function json(response) {
    const contentType = response.headers.get('Content-Type');
    if (response.status !== 204 && response.headers && contentType) {
        const type = parse(contentType).type;
        switch (type) {
            case 'application/json':
                return response.json();
            case 'text/plain':
                return response.text();
            case 'application/vnd.ms-excel':
                return response.blob();
            default:
                return response;
        }
    }
    return response;
}
/*
 * Set a function that will be called
 * before fetching a resource
 */
function before(fn) {
    beforeHook = fn;
}
/*
 * Set a function that will be called
 * after a resource has been fetched
 */
function after(fn) {
    afterHook = fn;
}
/*
 * Fetch a resource
 */
function fetchme(input, options = {}) {
    const isForm = options.body instanceof FormData;
    const headers = new Headers(options.headers || {});
    const token = options.token || read('token');
    if (token && !headers.get('Authorization')) {
        headers.set('Authorization', `Groups groups_token=${token}`);
    }
    const fetchOptions = Object.assign(Object.assign({}, options), {
        headers
    });
    if (options.body && !isForm && typeof options.body !== 'string') {
        console.warn('Converting JSON to string will be removed in a future version: use `json` instead of `body` parameter');
        options.json = options.body;
    }
    if (options.form) {
        headers.set('Content-Type', 'application/x-www-form-urlencoded');
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(options.form)) {
            searchParams.set(key, value);
        }
        fetchOptions.body = searchParams.toString();
    }
    if (options.json) {
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        fetchOptions.body = JSON.stringify(options.json);
    }
    beforeHook(fetchOptions);
    return fetch(input, fetchOptions)
        .then(status(input, fetchOptions))
        .then(response => {
        afterHook(fetchOptions);
        return response;
    })
        .then(json)
        .catch((error) => {
        let enhancedError = error;
        if (enhancedError instanceof TypeError &&
            networkErrorMessages.includes(enhancedError.message)) {
            enhancedError = new NetworkError(input, fetchOptions, enhancedError);
        }
        afterHook(fetchOptions, enhancedError);
    });
}

export { after, before, fetchme, json };
//# sourceMappingURL=fetchme.esm.js.map
